import React, { useState } from 'react'
import './MeasurementsPanel.css'

interface MeasurementsPanelProps {
  measurements: {
    foot_length?: number
    foot_width?: number
    arch_height?: number
    arch_classification?: string
    heel_width?: number
    instep_height?: number
    units?: string
    european_size?: number
    us_size_male?: number
    us_size_female?: number
  } | null
  loading?: boolean
}

const MeasurementsPanel: React.FC<MeasurementsPanelProps> = ({ measurements, loading }) => {
  const [isMinimized, setIsMinimized] = useState(true)

  if (loading) {
    return (
      <div className="measurements-panel">
        <div className="measurements-header">
          <div className="measurements-title-row">
            <h3>📏 Foot Measurements</h3>
            <button
              className="minimize-button"
              onClick={() => setIsMinimized(!isMinimized)}
              aria-label="Minimize panel"
            >
              −
            </button>
          </div>
        </div>
        <div className="measurements-loading">
          <div className="spinner"></div>
          <p>Calculating measurements...</p>
        </div>
      </div>
    )
  }

  if (!measurements) {
    return null
  }

  // If minimized, show only the restore button
  if (isMinimized) {
    return (
      <div className="measurements-panel minimized">
        <button
          className="restore-button"
          onClick={() => setIsMinimized(false)}
          aria-label="Restore panel"
          title="Show Foot Measurements"
        >
          📏
        </button>
      </div>
    )
  }

  const formatMeasurement = (value: number | undefined): string => {
    if (value === undefined) return 'N/A'
    return value.toFixed(1)
  }

  const getArchClassificationColor = (classification: string | undefined): string => {
    if (!classification) return '#666'
    switch (classification.toLowerCase()) {
      case 'low':
        return '#ff6b6b'
      case 'normal':
        return '#51cf66'
      case 'high':
        return '#339af0'
      default:
        return '#666'
    }
  }

  return (
    <div className="measurements-panel">
      <div className="measurements-header">
        <div className="measurements-title-row">
          <h3>📏 Foot Measurements</h3>
          <button
            className="minimize-button"
            onClick={() => setIsMinimized(true)}
            aria-label="Minimize panel"
            title="Minimize"
          >
            −
          </button>
        </div>
        <span className="measurements-units">All measurements in {measurements.units || 'mm'}</span>
      </div>

      <div className="measurements-content">
        <div className="measurements-grid">
        {/* Foot Length */}
        <div className="measurement-item">
          <div className="measurement-icon">📐</div>
          <div className="measurement-content">
            <div className="measurement-label">Foot Length</div>
            <div className="measurement-value">
              {formatMeasurement(measurements.foot_length)} <span className="unit">{measurements.units || 'mm'}</span>
            </div>
            <div className="measurement-description">Heel to longest toe</div>
          </div>
        </div>

        {/* Foot Width */}
        <div className="measurement-item">
          <div className="measurement-icon">↔️</div>
          <div className="measurement-content">
            <div className="measurement-label">Foot Width</div>
            <div className="measurement-value">
              {formatMeasurement(measurements.foot_width)} <span className="unit">{measurements.units || 'mm'}</span>
            </div>
            <div className="measurement-description">Maximum medial-lateral width</div>
          </div>
        </div>

        {/* Arch Height */}
        <div className="measurement-item">
          <div className="measurement-icon">🌉</div>
          <div className="measurement-content">
            <div className="measurement-label">Arch Height</div>
            <div className="measurement-value">
              {formatMeasurement(measurements.arch_height)} <span className="unit">{measurements.units || 'mm'}</span>
              {measurements.arch_classification && (
                <span 
                  className="arch-classification"
                  style={{ color: getArchClassificationColor(measurements.arch_classification) }}
                >
                  ({measurements.arch_classification})
                </span>
              )}
            </div>
            <div className="measurement-description">Ground to arch apex</div>
          </div>
        </div>

        {/* Heel Width */}
        <div className="measurement-item">
          <div className="measurement-icon">👠</div>
          <div className="measurement-content">
            <div className="measurement-label">Heel Width</div>
            <div className="measurement-value">
              {formatMeasurement(measurements.heel_width)} <span className="unit">{measurements.units || 'mm'}</span>
            </div>
            <div className="measurement-description">Width at heel region</div>
          </div>
        </div>

        {/* Instep Height */}
        <div className="measurement-item">
          <div className="measurement-icon">⬆️</div>
          <div className="measurement-content">
            <div className="measurement-label">Instep Height</div>
            <div className="measurement-value">
              {formatMeasurement(measurements.instep_height)} <span className="unit">{measurements.units || 'mm'}</span>
            </div>
            <div className="measurement-description">Maximum height at instep</div>
          </div>
        </div>
      </div>

      {/* Shoe Size Recommendations Section */}
      {(measurements.european_size || measurements.us_size_male || measurements.us_size_female) && (
        <div className="shoe-sizes-section">
          <div className="shoe-sizes-header">👟 Shoe Size Recommendations</div>
          <div className="shoe-sizes-grid">
            {/* European Size */}
            {measurements.european_size && (
              <div className="shoe-size-item">
                <div className="shoe-size-label">European</div>
                <div className="shoe-size-value">EU {measurements.european_size}</div>
              </div>
            )}

            {/* US Male Size */}
            {measurements.us_size_male && (
              <div className="shoe-size-item">
                <div className="shoe-size-label">US Men&apos;s</div>
                <div className="shoe-size-value">US {measurements.us_size_male}</div>
              </div>
            )}

            {/* US Female Size */}
            {measurements.us_size_female && (
              <div className="shoe-size-item">
                <div className="shoe-size-label">US Women&apos;s</div>
                <div className="shoe-size-value">US {measurements.us_size_female}</div>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  )
}

export default MeasurementsPanel

